"use client";

import { TheaterRailIconButton } from "@/components/feature/theater-rail-icon-button";
import { CtaPillButton } from "@/components/ui/cta-pill-button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  hasConfirmedClassroomShare,
  markClassroomShareConfirmed,
} from "@/lib/classroom-share-preference";
import { Z_INDEX_CLASSES } from "@/lib/constants/z-index";
import { cn } from "@/lib/utils";
import { X } from "@phosphor-icons/react";
import { type MouseEvent, useCallback, useState } from "react";

const CLASSROOM_ICON_SRC = "/images/icons/googleClassroom-icon.svg";

export function GameClassroomButton({ href }: { href: string }) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleRailClick = useCallback((event: MouseEvent<HTMLElement>) => {
    if (hasConfirmedClassroomShare()) return;
    event.preventDefault();
    setConfirmOpen(true);
  }, []);

  const handleConfirmShare = useCallback(() => {
    markClassroomShareConfirmed();
    setConfirmOpen(false);
    window.open(href, "_blank", "noopener,noreferrer");
  }, [href]);

  return (
    <div className="max-md:portrait:!hidden" data-testid="theater-rail-classroom-slot">
      <TheaterRailIconButton
        label="Classroom"
        aria-label="Add to Google Classroom"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="theater-rail-classroom"
        onClick={handleRailClick}
        icon={
          <img
            src={CLASSROOM_ICON_SRC}
            alt=""
            className="h-6 w-6 object-contain"
            data-testid="theater-rail-classroom-icon"
          />
        }
      />
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          overlayClassName={Z_INDEX_CLASSES.GAME_SHARE_SHEET}
          overlayTestId="game-classroom-confirm-overlay"
          className={cn(
            Z_INDEX_CLASSES.GAME_SHARE_SHEET,
            "max-w-sm gap-6 rounded-3xl border-2 border-black p-6 sm:rounded-3xl"
          )}
          data-testid="game-classroom-confirm"
        >
          <DialogClose
            className="absolute right-4 top-4 rounded-full p-1 text-ink-slate/70 hover:text-ink-slate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
            aria-label="Close"
            data-testid="game-classroom-confirm-close"
          >
            <X className="h-5 w-5" weight="bold" aria-hidden="true" />
          </DialogClose>
          <div className="flex justify-center pt-2">
            <img
              src={CLASSROOM_ICON_SRC}
              alt=""
              className="h-20 w-20 object-contain"
              data-testid="game-classroom-confirm-icon"
            />
          </div>
          <DialogHeader className="space-y-0 text-center sm:text-center">
            <DialogTitle className="charlie-kingdom-font text-balance text-center text-2xl font-bold leading-tight text-ink">
              Share to Google Classroom?
            </DialogTitle>
            <DialogDescription className="sr-only">
              Opens Google Classroom in a new tab.
            </DialogDescription>
          </DialogHeader>
          <div className="flex w-full gap-3">
            <CtaPillButton
              variant="secondary"
              size="sm"
              className="min-w-0 flex-1 text-center"
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </CtaPillButton>
            <CtaPillButton
              variant="primary"
              size="sm"
              className="min-w-0 flex-1 text-center"
              onClick={handleConfirmShare}
            >
              Share
            </CtaPillButton>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
